/**
 * Reference values a cost center can prefill into an order form (TUL-5597).
 *
 * Hero Tolk's Proweb migration carries per-company reference values that customers would
 * otherwise retype into every single order. The platform exposes them on each selectable
 * cost center (TUL-5596) as `invoiceRefDefault` / `externalOrderIdDefault`, and selecting
 * a cost center seeds the matching form field from them.
 *
 * The overwrite rule is the reason this module exists. A value the customer typed
 * themselves must survive a cost-center switch, while a value that is only in the field
 * because a *previous* cost center put it there must not — a form must never show one cost
 * center's reference while another one is selected. Telling those two apart is what
 * `previousDefault` is for: it is the default that was last applied, not the new one.
 *
 * Both group fields are absent until TUL-5596 ships. An absent default resolves to an empty
 * string, which leaves untouched fields empty and typed values alone — so this half can
 * land first without changing anything the customer sees.
 */

import { hasText } from './invoicingMandatoriness';

// Form field ← cost center field. `additionalInvoicingInfo` is the invoice reference input
// in both order wizards; `externalOrderId` is the customer's own order number.
export const USERGROUP_REFERENCE_DEFAULT_FIELDS = [
  { formField: 'additionalInvoicingInfo', groupField: 'invoiceRefDefault' },
  { formField: 'externalOrderId', groupField: 'externalOrderIdDefault' },
];

/**
 * The field pairs that may be prefilled for a given account.
 *
 * The external order ID input is only rendered when `user.userGroup.externalOrderIdUsed` is
 * set, but `formData.externalOrderId` reaches the payload either way
 * (`prebookingTransformers.js`, `translationOrderService.js`). Prefilling a field the
 * customer cannot see would therefore submit a value they never had a chance to review, so
 * an account that does not use external order IDs does not get that default at all.
 */
export const getUsergroupReferenceDefaultFields = ({ externalOrderIdUsed = false } = {}) =>
  USERGROUP_REFERENCE_DEFAULT_FIELDS.filter(
    ({ formField }) => formField !== 'externalOrderId' || Boolean(externalOrderIdUsed)
  );

/**
 * The value a field should hold once a cost center carrying `nextDefault` is selected.
 *
 * `previousDefault` is the default the previously selected cost center applied to this
 * field, empty when none has. Blank counts as untouched, by the same trimmed check that
 * decides whether a mandatory reference is satisfied — three spaces are not an edit worth
 * preserving over a real default.
 */
export const resolveDefaultedValue = (currentValue, previousDefault, nextDefault) => {
  const isUntouched = !hasText(currentValue) || currentValue === (previousDefault || '');
  return isUntouched ? (nextDefault || '') : currentValue;
};
